/**
 * GameWorld — the main game screen for students.
 * Houses the Phaser canvas + React overlay for battle UI.
 */
import { useEffect, useRef, useState, useCallback, useMemo, type CSSProperties } from 'react'
import { EventBus, EVENTS } from '@/game/EventBus'
import { useGame } from '@/context/GameContext'
import { standards, questions, attempts, events, feedback } from '@/api/client'
import type { Standard, Question } from '@/api/client'
import { getBadgeLabel, getBadgeDescription } from '@/config/badges'
import AccessibilityPanel from './AccessibilityPanel'
import BattleUI from './BattleUI'
import AvatarPicker from './AvatarPicker'
import SuperCivicaWorld from '@/game/SuperCivicaWorld'
import RealmCrossing, { CROSSING_REALMS } from './RealmCrossing'
import RealmIntro, { hasRealmIntro } from './RealmIntro'
import OverworldMusic from './OverworldMusic'

type ViewMode = 'overworld' | 'battle'

// Overlay/HUD text must track the a11y text-size setting. Every px font size
// in this file goes through an fs() from this hook (or the main component's
// identical helper) — never a hardcoded '<n>px' string, or the accessibility
// scale silently misses it (that's the bug class from the 2026-08 sweep).
function useFs() {
  const { textScale } = useGame()
  return (px: number) => `${(px * textScale).toFixed(1)}px`
}

// Biome → strand name used in realm-badge IDs (realm_<Strand>).
const BIOME_TO_STRAND: Record<string, string> = {
  geography: 'Geography', civics: 'Civics', economics: 'Economics', history: 'History',
}
// All realm badges, in display order, for the trophy shelf.
const ALL_REALM_BADGES = ['realm_Geography', 'realm_History', 'realm_Civics', 'realm_Economics', 'realm_all']

/** Realm-completion badges earned, derived live from mastery (no persistence needed).
 *  A realm is cleared when every node in its biome is proficient-or-mastered;
 *  realm_all is earned once all four realms are cleared. */
function earnedRealmBadges(stds: Standard[], myAttempts: Record<string, string>): string[] {
  const byBiome: Record<string, { total: number; done: number }> = {}
  for (const s of stds) {
    const b = s.biome ?? ''
    const cur = byBiome[b] ?? { total: 0, done: 0 }
    cur.total++
    const m = myAttempts[s.standard_id]
    if (m === 'mastered' || m === 'proficient') cur.done++
    byBiome[b] = cur
  }
  const out: string[] = []
  let cleared = 0
  for (const b in byBiome) {
    const strand = BIOME_TO_STRAND[b]
    if (!strand) continue
    if (byBiome[b].total > 0 && byBiome[b].done === byBiome[b].total) {
      out.push(`realm_${strand}`)
      cleared++
    }
  }
  if (cleared === 4) out.push('realm_all')
  return out
}

export default function GameWorld() {
  const { student, classInfo, logout, setLastXPEarned, avatar, setAvatar,
          textScale, muteMusic, setMuteMusic } = useGame()
  // Detect mobile once on mount (app doesn't support mid-session resize)
  const [isMobile] = useState(() => window.innerWidth < 640)
  const [view, setView]                 = useState<ViewMode>('overworld')
  const [allStandards, setAllStandards] = useState<Standard[]>([])
  const [myAttempts, setMyAttempts]     = useState<Record<string, string>>({})
  const [sceneReady, setSceneReady]     = useState(false)   // set true when SVG map mounts
  const [activeBattle, setActiveBattle] = useState<{
    standard: Standard
    questions: Question[]
  } | null>(null)
  const [xpPopup]           = useState<{ xp: number; visible: boolean }>({ xp: 0, visible: false })
  const [victoryScreen, setVictoryScreen] = useState<{
    visible: boolean
    victory: boolean
    xp: number
    realmCleared: boolean
    bossName: string
    biome: string
    newBadge?: string
  } | null>(null)
  const [menuOpen, setMenuOpen]             = useState(false)
  const [showTrophies, setShowTrophies]     = useState(false)
  const [trophyCelebration, setTrophyCelebration] = useState<string | null>(null)
  const [realmCinematic, setRealmCinematic]       = useState<string | null>(null)
  const [entrySplash, setEntrySplash]             = useState<{biome: string; bossName: string} | null>(null)
  const [realmCrossing, setRealmCrossing]         = useState<{from: string; to: string} | null>(null)
  // Pending crossing is stored in a ref so detection can write it during
  // handleBattleComplete without triggering a re-render; the cinematic chain
  // drains it after every preceding screen (realm-cleared, trophy) has finished.
  const pendingCrossingRef = useRef<{from: string; to: string} | null>(null)
  const battleStartRef = useRef<number>(0)  // Date.now() when battle UI opened (0 = unknown)
  const [showA11y, setShowA11y]             = useState(false)
  const [showTutorial, setShowTutorial]     = useState(true)
  const [noQuestionsAlert, setNoQuestionsAlert] = useState<string | null>(null)
  // Show avatar picker on first login (no stored avatar), re-openable from menu
  const [showAvatarPicker, setShowAvatarPicker] = useState(
    () => !localStorage.getItem('bb_avatar')
  )
  // One-tap post-battle feedback prompt (queued during handleBattleComplete,
  // shown after the cinematic chain returns to the overworld)
  const [pendingFeedback, setPendingFeedback] = useState<{ standardId: string; bossName: string } | null>(null)
  // Realm intro video: plays once per student before their FIRST battle in a
  // realm that has one registered (see RealmIntro.tsx). The clicked battle is
  // parked here while the video plays, then launched by onDone.
  const [realmIntro, setRealmIntro] = useState<string | null>(null)
  const pendingBattleRef = useRef<{ std: Standard; qs: Question[] } | null>(null)
  // The boss-click listener is registered once (empty-dep effect), so it reads
  // current progress through refs rather than stale closures.
  const myAttemptsRef = useRef(myAttempts)
  useEffect(() => { myAttemptsRef.current = myAttempts }, [myAttempts])
  const allStandardsRef = useRef(allStandards)
  useEffect(() => { allStandardsRef.current = allStandards }, [allStandards])
  const studentIdRef = useRef<number | null>(student?.id ?? null)
  useEffect(() => { studentIdRef.current = student?.id ?? null }, [student])

  // Seen-intro mark, scoped PER STUDENT: classroom Chromebooks are shared, so
  // a browser-wide key would let the first student consume every later
  // student's intro. This mark is only the abandon-guard; the durable
  // cross-device signal is the student's completed attempts in the biome.
  const seenIntrosKey = () => `bb_seen_realm_intros_${studentIdRef.current ?? 'anon'}`
  const seenWorldKey  = () => `bb_seen_world_intro_${studentIdRef.current ?? 'anon'}`
  const [worldIntro, setWorldIntro] = useState(false)

  // Launch a loaded battle: splash → battle UI (shared by the direct path and
  // the after-intro path). Stable identity — everything it touches is a ref
  // or setter — so the once-registered boss-click listener can safely call it.
  const launchBattle = useCallback((std: Standard, qs: Question[]) => {
    setActiveBattle({ standard: std, questions: qs })
    events.track('boss_start', { standard_id: std.standard_id })
    // Show a brief realm entry splash before revealing the battle UI
    setEntrySplash({ biome: std.biome ?? 'geography', bossName: std.boss_name })
    setTimeout(() => {
      setEntrySplash(null)
      // Clock starts when the battle UI is actually visible (splash excluded)
      battleStartRef.current = Date.now()
      setView('battle')
    }, 5800)
  }, [])

  // ── Preview shortcut: ?intro=history plays a realm intro video ─────────────
  useEffect(() => {
    const intro = new URLSearchParams(window.location.search).get('intro')
    if (intro && hasRealmIntro(intro)) setRealmIntro(intro)
  }, [])

  // First-login world intro (the 30s island fly-in). Skipped for e2e via
  // bb_skip_realm_intros, and once-per-student via localStorage.
  useEffect(() => {
    if (showAvatarPicker) return
    if (localStorage.getItem('bb_skip_realm_intros') === '1') return
    if (new URLSearchParams(window.location.search).get('intro')) return
    if (localStorage.getItem(seenWorldKey())) return
    setWorldIntro(true)
    // student id is in the key; re-run when login/avatar settle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAvatarPicker, student?.id])

  // ── Preview shortcut: ?crossing=civ (or geo-civ) fires the cinematic ────────
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('crossing')
    if (!raw) return
    if (raw.includes('-')) {
      // pair format: geo-civ
      const [from, to] = raw.split('-')
      if (CROSSING_REALMS[from] && CROSSING_REALMS[to]) setRealmCrossing({ from, to })
    } else {
      // destination-only: civ  →  default from = geo
      if (CROSSING_REALMS[raw]) setRealmCrossing({ from: 'geo', to: raw })
    }
  }, [])

  // ── Load standards + player progress ────────────────────────────────────────
  useEffect(() => {
    async function load() {
      // Load independently: a failed attempts call (e.g. transient network blip)
      // must not blank the whole world when standards loaded fine.
      const [stdsR, attsR] = await Promise.allSettled([
        standards.list(),
        attempts.mine(),
      ])

      if (stdsR.status === 'fulfilled') {
        setAllStandards(stdsR.value)
      } else {
        console.warn('Could not load standards from server — check the server is running', stdsR.reason)
      }

      if (attsR.status === 'fulfilled') {
        // Build mastery map: standardId → best mastery_status
        const masteryMap: Record<string, string> = {}
        for (const a of attsR.value) {
          if (!masteryMap[a.standard_id] ||
              masteryRank(a.mastery_status) > masteryRank(masteryMap[a.standard_id])) {
            masteryMap[a.standard_id] = a.mastery_status
          }
        }
        setMyAttempts(masteryMap)
      } else {
        console.warn('Could not load attempts — progress may look empty', attsR.reason)
      }
    }
    load()
  }, [])

  // ── Listen for Phaser scene-ready ────────────────────────────────────────
  useEffect(() => {
    const onReady = () => setSceneReady(true)
    EventBus.on(EVENTS.SCENE_READY, onReady)
    return () => { EventBus.off(EVENTS.SCENE_READY, onReady) }
  }, [])

  // (SuperCivicaWorld reads avatar, textScale, and standards directly — no Phaser push needed)

  // ── Viewport zoom lock: reset and lock zoom when battle opens, restore on close ──
  // Rewriting the meta content forces mobile browsers to snap back to 1× zoom,
  // and prevents the user from being trapped inside a pinch-zoomed battle card.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null
    if (!meta) return
    if (view === 'battle') {
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
    } else {
      meta.content = 'width=device-width, initial-scale=1.0, viewport-fit=cover'
    }
  }, [view])

  // ── Retreat from battle back to overworld ────────────────────────────────
  const handleRetreat = useCallback(() => {
    if (activeBattle) {
      events.track('boss_abandon', { standard_id: activeBattle.standard.standard_id })
      events.flush()
    }
    setView('overworld')
    setActiveBattle(null)
  }, [activeBattle])

  // Closing/hiding the tab mid-battle also counts as an abandon (kids close
  // Chromebook lids). Sent with keepalive so it survives the page teardown.
  useEffect(() => {
    if (view !== 'battle' || !activeBattle) return
    const standardId = activeBattle.standard.standard_id
    const onHide = () => {
      events.track('boss_abandon', { standard_id: standardId, meta: { via: 'pagehide' } })
      events.flush(true)
    }
    window.addEventListener('pagehide', onHide)
    return () => window.removeEventListener('pagehide', onHide)
  }, [view, activeBattle])

  // ── Listen for boss node click from Phaser ────────────────────────────────
  useEffect(() => {
    async function handleBossClick({ standardId }: { standardId: string }) {
      setShowTutorial(false)
      // Always close any in-progress battle before starting a new one
      setView('overworld')
      setActiveBattle(null)
      try {
        const [std, qs] = await Promise.all([
          standards.get(standardId),
          questions.forBattle(standardId),
        ])
        if (qs.length === 0) {
          setNoQuestionsAlert(`No questions yet for ${std.boss_name}! Ask your teacher to add some.`)
          setTimeout(() => setNoQuestionsAlert(null), 4000)
          return
        }
        // First battle in a realm with an intro video → play it, then launch.
        // "First" = no completed attempt in the biome (server-backed, follows
        // the student across devices) + a local mark so abandoning the first
        // battle doesn't replay the video on every click of this device.
        const biome = std.biome ?? 'geography'
        const seenIntros: string[] = JSON.parse(localStorage.getItem(seenIntrosKey()) || '[]')
        const attemptedInBiome = allStandardsRef.current.some(
          s => s.biome === biome && myAttemptsRef.current[s.standard_id],
        )
        // Escape hatch for tests/kiosk setups: bb_skip_realm_intros=1 disables
        // all intro videos on this browser.
        const skipAll = localStorage.getItem('bb_skip_realm_intros') === '1'
        if (!skipAll && hasRealmIntro(biome) && !attemptedInBiome && !seenIntros.includes(biome)) {
          localStorage.setItem(seenIntrosKey(), JSON.stringify([...seenIntros, biome]))
          pendingBattleRef.current = { std, qs }
          setRealmIntro(biome)
          return
        }
        launchBattle(std, qs)
      } catch (err) {
        // e.g. server-side progression gate (403 "Locked — defeat X first!")
        console.error('Failed to load battle', err)
        const msg = err instanceof Error ? err.message : 'Could not start the battle'
        setNoQuestionsAlert(msg)
        setTimeout(() => setNoQuestionsAlert(null), 4000)
      }
    }

    EventBus.on(EVENTS.BOSS_NODE_CLICK, handleBossClick)
    return () => { EventBus.off(EVENTS.BOSS_NODE_CLICK, handleBossClick) }
  }, [launchBattle])

  // ── Battle complete ───────────────────────────────────────────────────────
  const handleBattleComplete = useCallback(async (result: {
    score: number
    max_score: number
    items: unknown[]
    victory: boolean
  }) => {
    if (!activeBattle) return

    let xpEarned = 0
    let newMasteryMap: Record<string, string> = { ...myAttempts }

    try {
      const res = await attempts.save({
        standard_id: activeBattle.standard.standard_id,
        score: result.score,
        max_score: result.max_score,
        items: result.items,
        // Elapsed time in seconds; sent as a duration (not a timestamp) so a
        // wrong device clock can't corrupt time-on-task. Server derives started_at.
        duration_s: battleStartRef.current
          ? Math.max(0, Math.round((Date.now() - battleStartRef.current) / 1000))
          : undefined,
      })
      xpEarned = res.xp_earned

      newMasteryMap = { ...myAttempts, [activeBattle.standard.standard_id]: res.mastery }
      setMyAttempts(newMasteryMap)
      setLastXPEarned(res.xp_earned)
    } catch (err) {
      console.warn('Could not save attempt', err)
    }

    events.track('boss_complete', {
      standard_id: activeBattle.standard.standard_id,
      meta: {
        victory: result.victory,
        pct: result.max_score > 0 ? Math.round((result.score / result.max_score) * 100) : 0,
      },
    })
    // Queue the one-tap difficulty prompt for after the cinematic chain
    setPendingFeedback({
      standardId: activeBattle.standard.standard_id,
      bossName: activeBattle.standard.boss_name,
    })

    // Detect first-time realm unlock — destination-based, covers all crossings
    // including future realms. Tracks seen destination realm keys in bb_seen_realms.
    const BIOME_KEY: Record<string, string> = {
      geography: 'geo', civics: 'civ', economics: 'eco', history: 'his',
    }
    const fromKey = BIOME_KEY[activeBattle.standard.biome ?? ''] ?? 'geo'
    const seenRealms: string[] = JSON.parse(localStorage.getItem('bb_seen_realms') || '[]')

    // Group standards by biome for availability check
    const byBiome: Record<string, typeof allStandards> = {}
    for (const s of allStandards) {
      const b = s.biome ?? 'geography'
      ;(byBiome[b] = byBiome[b] ?? []).push(s)
    }

    const isAvailable = (s: typeof allStandards[0], masteryMap: Record<string,string>) =>
      s.prereq_ids.length === 0 ||
      s.prereq_ids.every(p => { const m = masteryMap[p]; return m && ['proficient','mastered'].includes(m) })

    for (const [biome, stds] of Object.entries(byBiome)) {
      if (biome === (activeBattle.standard.biome ?? 'geography')) continue  // same realm
      const toKey = BIOME_KEY[biome]
      if (!toKey || seenRealms.includes(toKey)) continue                    // already seen

      const wasLocked = !stds.some(s => isAvailable(s, myAttempts))
      const nowOpen   =  stds.some(s => isAvailable(s, newMasteryMap))

      if (wasLocked && nowOpen) {
        localStorage.setItem('bb_seen_realms', JSON.stringify([...seenRealms, toKey]))
        pendingCrossingRef.current = { from: fromKey, to: toKey }
        break   // only one crossing per battle
      }
    }

    // Detect realm cleared: all standards in this biome are now proficient or mastered
    const biome = activeBattle.standard.biome
    const biomeStandards = allStandards.filter(s => s.biome === biome)
    const realmCleared = biomeStandards.length > 0 && biomeStandards.every(s => {
      const m = newMasteryMap[s.standard_id]
      return m === 'proficient' || m === 'mastered'
    })

    // Newly earned realm badge (if any) — prefer the top-level "all realms" badge.
    const before = new Set(earnedRealmBadges(allStandards, myAttempts))
    const justEarned = earnedRealmBadges(allStandards, newMasteryMap).filter(b => !before.has(b))
    const newBadgeId = justEarned.includes('realm_all')
      ? 'realm_all'
      : justEarned.find(b => b.startsWith('realm_'))

    // Show victory/defeat screen
    setVictoryScreen({
      visible: true,
      victory: result.victory,
      xp: xpEarned,
      realmCleared,
      bossName: activeBattle.standard.boss_name,
      biome,
      newBadge: newBadgeId ? getBadgeLabel(newBadgeId) : undefined,
    })

    // After 3.5s: dismiss victory screen, then drain the cinematic chain in order:
    // realm-cleared → trophy → realm-crossing → map return
    // pendingCrossingRef holds a crossing discovered during this battle; it is
    // consumed by whichever onDone callback fires last in the chain.
    setTimeout(() => {
      setVictoryScreen(null)
      if (result.victory && realmCleared) {
        setRealmCinematic(activeBattle?.standard.biome ?? null)
        if (newBadgeId) setTrophyCelebration(getBadgeLabel(newBadgeId))
      } else if (result.victory && newBadgeId) {
        setTrophyCelebration(getBadgeLabel(newBadgeId))
      } else if (pendingCrossingRef.current) {
        // No realm-cleared or trophy — crossing fires directly after victory screen
        setRealmCrossing(pendingCrossingRef.current)
        pendingCrossingRef.current = null
      } else {
        setView('overworld')
        setActiveBattle(null)
      }
      // Refresh node mastery from server
      attempts.mine().then(myAtts => {
        const masteryMap: Record<string, string> = {}
        for (const a of myAtts) {
          if (!masteryMap[a.standard_id] ||
              masteryRank(a.mastery_status) > masteryRank(masteryMap[a.standard_id])) {
            masteryMap[a.standard_id] = a.mastery_status
          }
        }
        setMyAttempts(masteryMap)
      }).catch(() => {/* non-fatal */})
    }, 3500)
  }, [activeBattle, myAttempts, allStandards, setLastXPEarned])

  function masteryRank(m: string): number {
    return { mastered: 4, proficient: 3, developing: 2, attempted: 1, not_started: 0 }[m] ?? 0
  }

  const masteredCount   = Object.values(myAttempts).filter(m => m === 'mastered').length
  const proficientCount = Object.values(myAttempts).filter(m => ['mastered','proficient'].includes(m)).length
  const totalBattles    = allStandards.length
  const earnedBadges    = useMemo(() => new Set(earnedRealmBadges(allStandards, myAttempts)), [allStandards, myAttempts])

  // Scale HTML-overlay font sizes by the accessibility text-size multiplier,
  // the same way the SVG map labels scale. (px values otherwise ignore it.)
  const fs = (px: number) => `${(px * textScale).toFixed(1)}px`

  return (
    <div className="relative w-full overflow-hidden" style={{ background: '#0a0a1a', height: '100dvh' }}>

      {/* ── Super Civica World SVG map (starts below the HUD so labels aren't covered) ── */}
      <div className="absolute left-0 right-0 bottom-0" style={{ top: view === 'overworld' ? 56 : 0 }}>
        <SuperCivicaWorld standards={allStandards} myAttempts={myAttempts}/>
      </div>
      <OverworldMusic playing={
        view === 'overworld'
        && !worldIntro && !realmIntro && !entrySplash
        && !victoryScreen?.visible && !realmCinematic && !realmCrossing
        && !showAvatarPicker
      } />

      {/* ── Battle UI Overlay ──────────────────────────────────────────────── */}
      {/* position:fixed escapes the overflow-hidden root so touch-scroll works on iOS */}
      {view === 'battle' && activeBattle && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', touchAction: 'none' }}>
          <BattleUI
            standard={activeBattle.standard}
            questions={activeBattle.questions}
            onComplete={handleBattleComplete}
            onRetreat={handleRetreat}
            textScale={textScale}
          />
        </div>
      )}

      {/* ── HUD bar ────────────────────────────────────────────────────────── */}
      {view === 'overworld' && (
        <div className="absolute top-0 left-0 right-0 z-10"
             style={{
               height: 56, boxSizing: 'border-box',
               background: 'rgba(10,10,26,0.92)', borderBottom: '2px solid #1e1b4b',
               display: 'grid', gridTemplateColumns: '1fr auto 1fr',
               alignItems: 'center', padding: '0 14px', gap: 8,
             }}>

          {/* Left: title + divider + class name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden', justifySelf: 'start' }}>
            <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(9), color: '#a78bfa', whiteSpace: 'nowrap' }}>
              ⚔ SUPER CIVICA WORLD
            </span>
            {classInfo?.name && (
              <>
                <span style={{ width: 1, height: 18, background: '#312e81', flexShrink: 0 }}/>
                <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7.5), color: '#6366f1',
                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {classInfo.name}
                </span>
              </>
            )}
          </div>

          <div style={{
            justifySelf: 'center', pointerEvents: 'none',
            fontFamily: '"Press Start 2P", monospace', fontSize: fs(9),
            color: '#ffd840', letterSpacing: '1.5px',
            textShadow: '0 2px 0 #3a1a60', whiteSpace: 'nowrap',
          }}>
            SUPER CIVICA WORLD
          </div>

          {/* Right: labeled stat chips + player chip + menu */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, justifySelf: 'end' }}>

            {/* Stars chip */}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              background: '#0f0f23', border: '1px solid #1e1b4b', borderRadius: 6, padding: '4px 8px',
            }}>
              <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8), color: '#fbbf24', lineHeight: 1 }}>
                ★ {masteredCount}/{totalBattles}
              </span>
              <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7), color: '#6b7280', letterSpacing: '0.5px' }}>
                STARS
              </span>
            </div>

            {/* Bosses chip */}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              background: '#0f0f23', border: '1px solid #1e1b4b', borderRadius: 6, padding: '4px 8px',
            }}>
              <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8), color: '#34d399', lineHeight: 1 }}>
                ✔ {proficientCount}
              </span>
              <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7), color: '#6b7280', letterSpacing: '0.5px' }}>
                BOSSES
              </span>
            </div>

            {/* Player chip: avatar initial + name + level */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#0f0f23', border: '1px solid #312e81', borderRadius: 8, padding: '5px 9px',
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: 5, background: '#1e1b4b',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: '"Press Start 2P", monospace', fontSize: fs(7), color: '#a78bfa', flexShrink: 0,
              }}>
                {(student?.display_name?.[0] ?? '?').toUpperCase()}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7.5), color: '#fbbf24', lineHeight: 1, whiteSpace: 'nowrap' }}>
                  {student?.display_name}
                </span>
                <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7), color: '#818cf8', lineHeight: 1 }}>
                  LV {student?.level || 1}
                </span>
              </div>
            </div>

            {/* Map music mute */}
            <button
              onClick={() => setMuteMusic(!muteMusic)}
              aria-label={muteMusic ? 'Unmute map music' : 'Mute map music'}
              title={muteMusic ? 'Music off' : 'Music on'}
              style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8),
                       background: '#1e1b4b', border: '1px solid #312e81',
                       color: muteMusic ? '#6b7280' : '#fbbf24',
                       padding: '6px 8px', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }}>
              {muteMusic ? '🔇' : '🔊'}
            </button>

            {/* Menu button */}
            <button onClick={() => setMenuOpen(m => !m)}
              style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7),
                       background: '#1e1b4b', border: '1px solid #312e81',
                       color: '#818cf8', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }}>
              ☰
            </button>
          </div>
        </div>
      )}

      {/* ── Dropdown menu ──────────────────────────────────────────────────── */}
      {menuOpen && (
        <div className="absolute top-12 right-4 z-30 rounded-lg overflow-hidden"
             style={{ background: '#0f0f23', border: '2px solid #312e81', minWidth: 180 * textScale }}>
          {[
            { label: '🗺️ World Map',      action: () => { setView('overworld'); setMenuOpen(false) } },
            { label: `🏆 Trophies (${earnedBadges.size})`, action: () => { setShowTrophies(true); setMenuOpen(false) } },
            { label: '♿ Accessibility',  action: () => { setShowA11y(true); setMenuOpen(false) } },
            { label: '🎨 Customize Hero', action: () => { setShowAvatarPicker(true); setMenuOpen(false) } },
            { label: '🚪 Log Out',        action: logout },
          ].map(item => (
            <button key={item.label} onClick={item.action}
              className="w-full text-left px-4 py-3 transition-colors hover:bg-indigo-900"
              style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7), color: '#e0e7ff',
                       display: 'block', cursor: 'pointer', background: 'transparent', border: 'none' }}>
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Accessibility panel ────────────────────────────────────────────── */}
      {showA11y && <AccessibilityPanel onClose={() => setShowA11y(false)} />}

      {/* ── Tutorial prompt — shown until first boss click ──────────────────── */}
      {view === 'overworld' && showTutorial && sceneReady && allStandards.length > 0 && (
        <div
          className="absolute z-10"
          style={{
            bottom: isMobile ? 172 : 48,  // clear the mobile bottom dock
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(10,10,26,0.92)',
            border: '2px solid #6366f1',
            borderRadius: 10,
            padding: '12px 20px',
            textAlign: 'center',
            pointerEvents: 'none',
            animation: 'pulse 2s ease-in-out infinite',
          }}>
          <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8), color: '#fbbf24', marginBottom: 6 }}>
            🗡️ QUEST: DEFEAT YOUR FIRST BOSS!
          </div>
          <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(6), color: '#818cf8' }}>
            Click a landmark on the island to start a battle
          </div>
          <button
            onClick={() => setShowTutorial(false)}
            style={{ marginTop: 8, fontFamily: '"Press Start 2P", monospace', fontSize: fs(6),
                     color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer',
                     pointerEvents: 'all' }}>
            [dismiss]
          </button>
        </div>
      )}

      {/* ── No-questions alert ─────────────────────────────────────────────── */}
      {noQuestionsAlert && (
        <div className="absolute z-40"
             style={{
               top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
               background: '#1e1b4b', border: '2px solid #fbbf24',
               borderRadius: 10, padding: '20px 28px', textAlign: 'center',
               maxWidth: 320,
             }}>
          <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8), color: '#fbbf24', marginBottom: 8 }}>
            ⚠️ NO QUESTIONS YET
          </div>
          <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(6), color: '#e0e7ff', lineHeight: 1.8 }}>
            {noQuestionsAlert}
          </div>
        </div>
      )}

      {/* ── World intro (once per student, after avatar picker) ── */}
      {worldIntro && (
        <RealmIntro
          biome="world"
          onDone={() => {
            localStorage.setItem(seenWorldKey(), '1')
            setWorldIntro(false)
          }}
        />
      )}

      {/* ── Realm intro video (first battle in a realm, once per student) ── */}
      {realmIntro && (
        <RealmIntro
          biome={realmIntro}
          onDone={() => {
            setRealmIntro(null)
            const pending = pendingBattleRef.current
            pendingBattleRef.current = null
            if (pending) launchBattle(pending.std, pending.qs)
          }}
        />
      )}

      {/* ── Realm entry splash ────────────────────────────────────────────── */}
      {entrySplash && (
        <RealmEntrySplash biome={entrySplash.biome} bossName={entrySplash.bossName}/>
      )}

      {/* ── Victory / Defeat screen ────────────────────────────────────────── */}
      {victoryScreen?.visible && (
        <VictoryScreen
          victory={victoryScreen.victory}
          xp={victoryScreen.xp}
          bossName={victoryScreen.bossName}
          realmCleared={victoryScreen.realmCleared}
          biome={victoryScreen.biome}
          newBadge={victoryScreen.newBadge}
        />
      )}

      {/* ── One-tap post-battle feedback (non-blocking, auto-dismisses) ────── */}
      {view === 'overworld' && pendingFeedback && !victoryScreen?.visible &&
        !realmCinematic && !trophyCelebration && !realmCrossing && !entrySplash && (
        <FeedbackToast
          bossName={pendingFeedback.bossName}
          textScale={textScale}
          onPick={(rating) => {
            feedback.submit({
              source: 'student_boss',
              standard_id: pendingFeedback.standardId,
              rating,
            }).catch(() => {/* best-effort */})
            setPendingFeedback(null)
          }}
          onConfused={() => {
            feedback.submit({
              source: 'student_confusion',
              standard_id: pendingFeedback.standardId,
              rating: 1,
            }).catch(() => {/* best-effort */})
            setPendingFeedback(null)
          }}
          onDismiss={() => setPendingFeedback(null)}
        />
      )}

      {/* ── Realm-cleared cinematic (fires after victory screen, before map return) */}
      {realmCinematic && (
        <RealmClearedCinematic
          biome={realmCinematic}
          onDone={() => {
            setRealmCinematic(null)
            if (trophyCelebration) {
              // trophy already queued — it will render next
            } else if (pendingCrossingRef.current) {
              setRealmCrossing(pendingCrossingRef.current)
              pendingCrossingRef.current = null
            } else {
              setView('overworld')
              setActiveBattle(null)
            }
          }}
        />
      )}

      {/* ── Trophy unlocked celebration ── */}
      {!realmCinematic && trophyCelebration && (
        <TrophyCelebration
          badgeLabel={trophyCelebration}
          onDone={() => {
            setTrophyCelebration(null)
            if (pendingCrossingRef.current) {
              setRealmCrossing(pendingCrossingRef.current)
              pendingCrossingRef.current = null
            } else {
              setView('overworld')
              setActiveBattle(null)
            }
          }}
        />
      )}

      {/* ── Realm border-crossing cinematic (fires once when a new realm unlocks) */}
      {realmCrossing && (
        <RealmCrossing
          from={realmCrossing.from}
          to={realmCrossing.to}
          duration={7000}
          onDone={() => {
            setRealmCrossing(null)
            setView('overworld')
            setActiveBattle(null)
          }}
        />
      )}

      {/* ── XP popup ───────────────────────────────────────────────────────── */}
      {xpPopup.visible && (
        <div className="absolute top-20 right-8 z-40 px-4 py-3 rounded-lg"
             style={{ background: '#1e1b4b', border: '2px solid #fbbf24',
                      fontFamily: '"Press Start 2P", monospace', fontSize: fs(11), color: '#fbbf24',
                      animation: 'bounce 0.5s ease infinite alternate' }}>
          ⭐ +{xpPopup.xp} XP!
        </div>
      )}

      {/* ── Controls hint — desktop only (mobile has bottom dock) ────────── */}
      {view === 'overworld' && !isMobile && (
        <div className="absolute bottom-3 left-4 right-4 z-10">
          <span style={{
            fontFamily: '"Press Start 2P", monospace', fontSize: fs(7.5), color: '#e0e7ff',
            background: 'rgba(10,10,35,0.72)', backdropFilter: 'blur(4px)',
            borderRadius: 6, padding: '5px 10px',
            border: '1px solid rgba(99,102,241,0.3)',
            display: 'inline-block',
          }}>
            Click a Castle or ? Block to battle · Grey = locked (complete prereqs first)
          </span>
        </div>
      )}

      {/* ── CSS animations ──────────────────────────────────────────────────── */}
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 8px #6366f1; }
          50%       { box-shadow: 0 0 24px #a78bfa; }
        }
        @keyframes victoryDrop {
          0%   { transform: translateY(-60px) scale(0.7); opacity: 0; }
          60%  { transform: translateY(10px) scale(1.08); opacity: 1; }
          80%  { transform: translateY(-4px) scale(0.97); }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes xpCount {
          0%   { transform: scale(0.5); opacity: 0; }
          50%  { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes starFall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes realmFlash {
          0%, 100% { opacity: 1; text-shadow: 0 0 20px #fbbf24, 0 0 40px #f59e0b; }
          50%       { opacity: 0.7; text-shadow: 0 0 60px #fbbf24, 0 0 100px #f59e0b; }
        }
        @keyframes defeatShake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-8px); }
          40%       { transform: translateX(8px); }
          60%       { transform: translateX(-5px); }
          80%       { transform: translateX(5px); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes entrySplashIn {
          from { opacity: 0; transform: scale(1.06); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes entrySplashOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
      `}</style>

      {/* ── Trophy shelf — realm-completion badges ──────────────────────────── */}
      {showTrophies && (
        <div className="absolute inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(5,5,20,0.85)' }}
             onClick={() => setShowTrophies(false)}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#0f0f23', border: '2px solid #312e81', borderRadius: 12,
                        padding: '20px 22px', maxWidth: 360, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(11), color: '#fbbf24',
                          textAlign: 'center', marginBottom: 4 }}>
              🏆 TROPHY SHELF
            </div>
            <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7.5), color: '#6b7280',
                          textAlign: 'center', marginBottom: 16 }}>
              {earnedBadges.size} / {ALL_REALM_BADGES.length} earned
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ALL_REALM_BADGES.map(id => {
                const earned = earnedBadges.has(id)
                return (
                  <div key={id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8,
                    background: earned ? 'rgba(251,191,36,0.10)' : 'rgba(255,255,255,0.03)',
                    border: earned ? '1px solid #fbbf24' : '1px solid #1e1b4b',
                    opacity: earned ? 1 : 0.55,
                  }}>
                    <span style={{ fontSize: 22, filter: earned ? 'none' : 'grayscale(1)' }} aria-hidden>
                      {id === 'realm_all' ? '👑' : '🏅'}
                    </span>
                    <div>
                      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7),
                                    color: earned ? '#fbbf24' : '#818cf8', marginBottom: 4 }}>
                        {earned ? getBadgeLabel(id) : '???'}
                      </div>
                      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7.5),
                                    color: '#6b7280', lineHeight: 1.7 }}>
                        {getBadgeDescription(id)}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <button onClick={() => setShowTrophies(false)}
              style={{ marginTop: 16, width: '100%', fontFamily: '"Press Start 2P", monospace', fontSize: fs(7),
                       color: '#e0e7ff', background: '#1e1b4b', border: '1px solid #312e81',
                       borderRadius: 6, padding: '10px', cursor: 'pointer' }}>
              CLOSE
            </button>
          </div>
        </div>
      )}

      {/* ── Avatar picker — first login or from menu ────────────────────────── */}
      {showAvatarPicker && (
        <AvatarPicker
          initial={avatar}
          saveLabel={localStorage.getItem('bb_avatar') ? 'UPDATE HERO' : 'SAVE HERO'}
          onSave={a => {
            setAvatar(a)
            setShowAvatarPicker(false)
          }}
        />
      )}
    </div>
  )
}

// ── Victory / Defeat overlay ──────────────────────────────────────────────────

const BIOME_LABELS: Record<string, string> = {
  history:   '📜 HISTORY REALM',
  geography: '🌍 GEOGRAPHY REALM',
  civics:    '⚖️ CIVICS REALM',
  economics: '💰 ECONOMICS REALM',
}

function VictoryScreen({ victory, xp, bossName, realmCleared, biome, newBadge }: {
  victory: boolean
  xp: number
  bossName: string
  realmCleared: boolean
  biome: string
  newBadge?: string
}) {
  const fs = useFs()
  // Generate star positions once (stable array)
  const stars = Array.from({ length: 28 }, (_, i) => ({
    left: `${(i * 37 + 7) % 100}%`,
    delay: `${(i * 0.13) % 1.4}s`,
    duration: `${1.6 + (i * 0.11) % 1.2}s`,
    size: `${12 + (i * 7) % 16}px`,
    char: ['⭐','✨','🌟','💫'][(i) % 4],
  }))

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 60,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: victory
        ? 'radial-gradient(ellipse at center, rgba(16,40,80,0.97) 0%, rgba(5,5,20,0.98) 100%)'
        : 'radial-gradient(ellipse at center, rgba(60,10,10,0.97) 0%, rgba(5,5,20,0.98) 100%)',
      overflow: 'hidden',
    }}>

      {/* Falling stars (victory only) */}
      {victory && stars.map((s, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: '-30px',
          left: s.left,
          fontSize: s.size,
          animation: `starFall ${s.duration} ${s.delay} ease-in forwards`,
          pointerEvents: 'none',
        }}>
          {s.char}
        </div>
      ))}

      {/* Realm cleared banner (appears above main title) */}
      {realmCleared && victory && (
        <div style={{
          fontFamily: '"Press Start 2P", monospace',
          fontSize: fs(11),
          color: '#fbbf24',
          marginBottom: 18,
          animation: 'realmFlash 1s ease-in-out infinite, fadeInUp 0.5s ease forwards',
          textAlign: 'center',
          letterSpacing: 2,
        }}>
          🏆 {BIOME_LABELS[biome] || 'REALM'} CLEARED! 🏆
        </div>
      )}

      {/* New trophy earned */}
      {newBadge && victory && (
        <div style={{
          fontFamily: '"Press Start 2P", monospace',
          fontSize: fs(8),
          color: '#34d399',
          marginBottom: 18,
          padding: '8px 16px',
          borderRadius: 8,
          background: 'rgba(52,211,153,0.10)',
          border: '1px solid #34d399',
          animation: 'fadeInUp 0.5s ease 0.3s both',
          textAlign: 'center',
        }}>
          🏅 NEW TROPHY: {newBadge}
        </div>
      )}

      {/* Main result title */}
      <div style={{
        fontFamily: '"Press Start 2P", monospace',
        fontSize: victory ? fs(22) : fs(18),
        color: victory ? '#34d399' : '#ef4444',
        textAlign: 'center',
        lineHeight: 1.4,
        animation: victory
          ? 'victoryDrop 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards'
          : 'defeatShake 0.6s ease 0.2s forwards',
        marginBottom: 16,
        textShadow: victory
          ? '0 0 30px rgba(52,211,153,0.6), 0 0 60px rgba(52,211,153,0.3)'
          : '0 0 20px rgba(239,68,68,0.5)',
      }}>
        {victory ? '⚔️ BOSS DEFEATED!' : '💀 DEFEATED...'}
      </div>

      {/* Boss name */}
      <div style={{
        fontFamily: '"Press Start 2P", monospace',
        fontSize: fs(8),
        color: '#818cf8',
        marginBottom: 24,
        animation: 'fadeInUp 0.4s ease 0.4s both',
      }}>
        {bossName}
      </div>

      {/* XP earned */}
      {xp > 0 && (
        <div style={{
          fontFamily: '"Press Start 2P", monospace',
          fontSize: fs(14),
          color: '#fbbf24',
          background: 'rgba(30,27,74,0.8)',
          border: '2px solid #fbbf24',
          borderRadius: 10,
          padding: '12px 28px',
          animation: 'xpCount 0.5s ease 0.7s both',
          boxShadow: '0 0 30px rgba(251,191,36,0.3)',
        }}>
          ⭐ +{xp} XP
        </div>
      )}

      {/* Returning text */}
      <div style={{
        position: 'absolute',
        bottom: 28,
        fontFamily: '"Press Start 2P", monospace',
        fontSize: fs(6),
        color: '#374151',
        animation: 'fadeInUp 0.4s ease 1.5s both',
      }}>
        Returning to world map…
      </div>
    </div>
  )
}

// ── Trophy celebration overlay ────────────────────────────────────────────────
const TROPHY_EMOJIS = ['🏆','🏅','🌟','⭐','✨','💫','🎉','🎊']

function TrophyCelebration({ badgeLabel, onDone }: { badgeLabel: string; onDone: () => void }) {
  const fs = useFs()
  const bursts = Array.from({ length: 40 }, (_, i) => ({
    emoji: TROPHY_EMOJIS[i % TROPHY_EMOJIS.length],
    left:  `${(i * 43 + 3) % 100}%`,
    delay: `${(i * 0.07) % 1.2}s`,
    dur:   `${1.4 + (i * 0.09) % 1.0}s`,
    size:  `${14 + (i * 9) % 18}px`,
  }))

  useEffect(() => {
    const t = setTimeout(onDone, 7200)
    return () => clearTimeout(t)
  }, [onDone])

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 80,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at center, rgba(40,30,5,0.97) 0%, rgba(5,5,20,0.98) 100%)',
      overflow: 'hidden',
    }}
      onClick={onDone}
    >
      {bursts.map((b, i) => (
        <div key={i} style={{
          position: 'absolute', top: '-40px', left: b.left, fontSize: b.size,
          animation: `starFall ${b.dur} ${b.delay} ease-in forwards`, pointerEvents: 'none',
        }}>{b.emoji}</div>
      ))}
      <div style={{ fontSize: 96, marginBottom: 16,
        animation: 'victoryDrop 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards',
        filter: 'drop-shadow(0 0 30px #fbbf24)' }}>🏆</div>
      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(14),
        color: '#fbbf24', textAlign: 'center', lineHeight: 1.5, marginBottom: 12,
        textShadow: '0 0 20px #fbbf24, 0 0 50px #f59e0b',
        animation: 'realmFlash 1.2s ease-in-out infinite, fadeInUp 0.4s ease 0.3s both' }}>
        TROPHY UNLOCKED!
      </div>
      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(9),
        color: '#e0e7ff', textAlign: 'center', lineHeight: 1.8,
        padding: '10px 24px', borderRadius: 10, maxWidth: 320,
        background: 'rgba(251,191,36,0.12)', border: '2px solid #fbbf24',
        animation: 'fadeInUp 0.4s ease 0.6s both' }}>
        {badgeLabel}
      </div>
      <div style={{ position: 'absolute', bottom: 28,
        fontFamily: '"Press Start 2P", monospace', fontSize: fs(6), color: '#4b5563',
        animation: 'fadeInUp 0.4s ease 1.8s both' }}>tap anywhere to continue</div>
    </div>
  )
}

// ── Realm-cleared cinematic ───────────────────────────────────────────────────
const REALM_THEMES: Record<string, {
  label: string; icon: string; color: string; bg: string; particles: string[]
  enter: string
}> = {
  geography: {
    label: 'GEOGRAPHY FRONTIER', icon: '🌍', color: '#3aa83a',
    bg: 'radial-gradient(ellipse at center, rgba(10,40,10,0.97) 0%, rgba(5,15,5,0.98) 100%)',
    particles: ['🌿','🗺️','⛰️','🌲','🌊','🌍','⭐','✨'],
    enter: 'ENTERING THE FRONTIER',
  },
  civics: {
    label: 'CIVICS CAPITOL', icon: '⚖️', color: '#8c4eb8',
    bg: 'radial-gradient(ellipse at center, rgba(25,10,40,0.97) 0%, rgba(5,5,20,0.98) 100%)',
    particles: ['⚖️','🏛️','📜','🗳️','🔔','⭐','✨','💫'],
    enter: 'ENTERING THE CAPITOL',
  },
  economics: {
    label: 'ECO EXCHANGE', icon: '💰', color: '#d44a2a',
    bg: 'radial-gradient(ellipse at center, rgba(40,20,5,0.97) 0%, rgba(20,5,5,0.98) 100%)',
    particles: ['💰','🪙','💎','📈','🏪','⭐','✨','🌟'],
    enter: 'ENTERING THE MARKET',
  },
  history: {
    label: 'HISTORY REALM', icon: '📜', color: '#c08a2a',
    bg: 'radial-gradient(ellipse at center, rgba(35,25,5,0.97) 0%, rgba(15,10,5,0.98) 100%)',
    particles: ['📜','🏺','⚔️','🗿','🏛️','⭐','✨','🌟'],
    enter: 'ENTERING THE PYRAMID',
  },
}

function RealmClearedCinematic({ biome, onDone }: { biome: string; onDone: () => void }) {
  const fs = useFs()
  const theme = REALM_THEMES[biome] ?? REALM_THEMES.geography
  const particles = Array.from({ length: 36 }, (_, i) => ({
    emoji: theme.particles[i % theme.particles.length],
    left: `${(i * 41 + 5) % 100}%`,
    delay: `${(i * 0.08) % 1.0}s`,
    dur: `${1.6 + (i * 0.1) % 1.2}s`,
    size: `${16 + (i * 7) % 20}px`,
  }))

  useEffect(() => {
    const t = setTimeout(onDone, 8200)
    return () => clearTimeout(t)
  }, [onDone])

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 75,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: theme.bg, overflow: 'hidden',
    }}
      onClick={onDone}
    >
      {particles.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: '-40px', left: p.left, fontSize: p.size,
          animation: `starFall ${p.dur} ${p.delay} ease-in forwards`, pointerEvents: 'none',
        }}>{p.emoji}</div>
      ))}
      <div style={{ fontSize: 80, marginBottom: 8,
        animation: 'victoryDrop 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards',
        filter: `drop-shadow(0 0 30px ${theme.color})` }}>{theme.icon}</div>
      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(16),
        color: theme.color, textAlign: 'center', lineHeight: 1.5, marginBottom: 10,
        textShadow: `0 0 20px ${theme.color}, 0 0 50px ${theme.color}`,
        animation: 'realmFlash 1.2s ease-in-out infinite, fadeInUp 0.4s ease 0.4s both' }}>
        REALM CLEARED!
      </div>
      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(10),
        color: '#e0e7ff', textAlign: 'center', letterSpacing: 2, marginBottom: 24,
        animation: 'fadeInUp 0.4s ease 0.7s both' }}>{theme.label}</div>
      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7),
        color: '#6b7280', animation: 'fadeInUp 0.4s ease 1.4s both' }}>tap to continue</div>
    </div>
  )
}

// ── Realm entry splash ────────────────────────────────────────────────────────
// Brief 1.8s title card shown when a battle starts. Slides in, holds, fades out.
function RealmEntrySplash({ biome, bossName }: { biome: string; bossName: string }) {
  const fs = useFs()
  const theme = REALM_THEMES[biome] ?? REALM_THEMES.geography
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 65,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: theme.bg, overflow: 'hidden',
      animation: 'entrySplashIn 0.35s ease forwards, entrySplashOut 0.4s ease 5.4s forwards',
      pointerEvents: 'none',
    }}>
      {/* Realm icon */}
      <div style={{ fontSize: 64, marginBottom: 12,
        animation: 'victoryDrop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.1s both',
        filter: `drop-shadow(0 0 20px ${theme.color})` }}>{theme.icon}</div>

      {/* Realm name */}
      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8),
        color: theme.color, letterSpacing: 3, marginBottom: 16,
        textShadow: `0 0 12px ${theme.color}`,
        animation: 'fadeInUp 0.3s ease 0.2s both' }}>{theme.label}</div>

      {/* Divider */}
      <div style={{ width: 120, height: 2, background: theme.color, opacity: 0.5,
        marginBottom: 16, animation: 'fadeInUp 0.3s ease 0.3s both' }}/>

      {/* Boss name */}
      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(11),
        color: '#fff', textAlign: 'center', lineHeight: 1.6, maxWidth: 420,
        animation: 'fadeInUp 0.3s ease 0.4s both' }}>{bossName}</div>

      <div style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(7),
        color: '#fbbf24', letterSpacing: 2, marginTop: 20,
        animation: 'realmFlash 0.8s ease-in-out infinite, fadeInUp 0.3s ease 0.7s both' }}>
        {theme.enter}
      </div>
    </div>
  )
}

/** One-tap post-battle difficulty check — the durable feedback loop from
 *  docs/product/FEEDBACK_KIT.md. Never blocks gameplay: one tap to answer,
 *  the ✕ (or 12s of ignoring it) dismisses. */
function FeedbackToast({ bossName, textScale, onPick, onConfused, onDismiss }: {
  bossName: string
  textScale: number
  onPick: (rating: -1 | 0 | 1) => void
  onConfused: () => void
  onDismiss: () => void
}) {
  // Auto-dismiss after 12s so it never lingers into the next battle
  useEffect(() => {
    const t = setTimeout(onDismiss, 12000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fs = (px: number) => `${(px * textScale).toFixed(1)}px`
  const btn: CSSProperties = {
    fontFamily: '"Press Start 2P", monospace', fontSize: fs(7.5),
    background: '#1e1b4b', border: '1px solid #312e81', color: '#e0e7ff',
    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  }

  return (
    <div style={{
      position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
      zIndex: 25, background: 'rgba(10,10,26,0.96)', border: '2px solid #312e81',
      borderRadius: 12, padding: '12px 14px', maxWidth: 420, width: 'calc(100% - 24px)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.6)', animation: 'fadeInUp 0.3s ease both',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8), color: '#a78bfa' }}>
          HOW WAS {bossName.toUpperCase()}?
        </span>
        <button onClick={onDismiss} aria-label="Skip"
          style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(8), background: 'none',
                   border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4 }}>
          ✕
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button style={btn} onClick={() => onPick(-1)}>
          <span style={{ fontSize: fs(16) }}>😴</span><span>TOO EASY</span>
        </button>
        <button style={btn} onClick={() => onPick(0)}>
          <span style={{ fontSize: fs(16) }}>😎</span><span>JUST RIGHT</span>
        </button>
        <button style={btn} onClick={() => onPick(1)}>
          <span style={{ fontSize: fs(16) }}>🔥</span><span>TOO HARD</span>
        </button>
      </div>
      <button onClick={onConfused}
        style={{ fontFamily: '"Press Start 2P", monospace', fontSize: fs(6.5), background: 'none',
                 border: 'none', color: '#6b7280', cursor: 'pointer', marginTop: 8,
                 width: '100%', textAlign: 'center', textDecoration: 'underline' }}>
        something was confusing
      </button>
    </div>
  )
}
